// PurchaseSimulationView.js — 발주 산출에서 체크한 품목만 추려 "월평균 판매량 / 리드타임"을 바꿔가며
// 권장 발주 수량이 어떻게 달라지는지 시험해 보는 전체화면 시뮬레이터.
// PurchasePlanHistoryView / WeeklyReportView와 같은 오버레이 패턴(fixed inset-0)이며,
// 여기서 바꾼 값은 앱 데이터에 저장되지 않는다(원본 발주 산출 화면에는 영향 없음).
//
// 계산식은 App.js의 reorderRows(설계서 수식 2026-07-14)와 동일하게 유지해야 한다:
//   d̄ = 월평균 ÷ 30 · σ = CV × d̄ (월평균을 바꿔도 변동계수는 유지)
//   SS = z × σ × √LT (Y등급 z+0.5) · ROP = d̄×LT + SS · T = d̄×목표일수 + SS
//   Q = T + d̄×LT − (현재고 + 발주잔량)
import React, { useMemo, useState } from "react";
import { X, SlidersHorizontal, FileSpreadsheet, RotateCcw } from "lucide-react";
import XLSXStyle from "xlsx-js-style";

const Z_TABLE = { 90: 1.28, 95: 1.65, 99: 2.33 };
const nf = (n) => (Number(n) || 0).toLocaleString("ko-KR");
const dc = (n, d = 1) => (n == null || !isFinite(n) ? "—" : Number(n).toFixed(d));
const toNum = (v) => {
  if (v == null || v === "") return 0;
  const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
  return isNaN(n) ? 0 : n;
};

export default function PurchaseSimulationView({ onClose, rows = [], cfg = {} }) {
  const [svc, setSvc] = useState(cfg.svc || 95);
  const [targetDays, setTargetDays] = useState(cfg.targetDays || 30);
  const [bulkPct, setBulkPct] = useState(100);   // 월평균 일괄 배수(%)
  const [bulkLtAdd, setBulkLtAdd] = useState(0); // 리드타임 일괄 가감(일)
  const [mOv, setMOv] = useState({});            // 품목별 월평균 직접 입력
  const [ltOv, setLtOv] = useState({});          // 품목별 리드타임 직접 입력

  const resetAll = () => {
    setSvc(cfg.svc || 95);
    setTargetDays(cfg.targetDays || 30);
    setBulkPct(100);
    setBulkLtAdd(0);
    setMOv({});
    setLtOv({});
  };

  const sim = useMemo(() => {
    const z = Z_TABLE[svc] || 1.65;
    return rows.map((r) => {
      const baseMonthly = Math.round((r.dAvg || 0) * 30);
      const monthly = mOv[r.code] != null && mOv[r.code] !== ""
        ? Math.max(0, toNum(mOv[r.code]))
        : Math.max(0, Math.round(baseMonthly * (toNum(bulkPct) || 0) / 100));
      const lt = ltOv[r.code] != null && ltOv[r.code] !== ""
        ? Math.max(1, toNum(ltOv[r.code]))
        : Math.max(1, (r.lt || 1) + toNum(bulkLtAdd));
      const dAvg = monthly / 30;
      // 월평균을 바꿔도 판매 변동성(CV)은 그대로 유지 → σ는 판매량에 비례해 함께 움직인다.
      const sd = r.cv != null ? r.cv * dAvg : 0;
      const zEff = r.grade === "Y" ? z + 0.5 : z;
      const ss = dAvg > 0 ? zEff * sd * Math.sqrt(lt) : 0;
      const rop = dAvg * lt + ss;
      const target = dAvg * targetDays + ss;
      const onhand = r.onhand || 0;
      const qty = dAvg > 0 ? Math.max(0, Math.ceil(target + dAvg * lt - onhand)) : 0;
      const runout = dAvg > 0 ? onhand / dAvg : null;
      let signal = "여유";
      if (dAvg > 0 && onhand <= rop) signal = "지금발주";
      else if (dAvg > 0 && onhand <= rop * 1.2) signal = "곧발주";
      const touched = (mOv[r.code] != null && mOv[r.code] !== "") || (ltOv[r.code] != null && ltOv[r.code] !== "");
      return { ...r, baseMonthly, baseQty: r.qty || 0, baseLT: r.lt, monthly, lt, dAvg, sd, ss, rop, target, qty, runout, signal, touched, diff: qty - (r.qty || 0) };
    });
  }, [rows, svc, targetDays, bulkPct, bulkLtAdd, mOv, ltOv]);

  const sum = useMemo(() => {
    const baseQty = sim.reduce((s, r) => s + r.baseQty, 0);
    const simQty = sim.reduce((s, r) => s + r.qty, 0);
    const now = sim.filter((r) => r.signal === "지금발주").length;
    const covers = sim.filter((r) => r.runout != null).map((r) => r.runout);
    return {
      baseQty, simQty, diff: simQty - baseQty,
      pct: baseQty > 0 ? ((simQty - baseQty) / baseQty) * 100 : null,
      now, avgCover: covers.length ? covers.reduce((a, b) => a + b, 0) / covers.length : null,
    };
  }, [sim]);

  const exportXlsx = () => {
    try {
      if (sim.length === 0) return;
      const hdrs = ["상품코드", "상품명", "A코드", "현재고", "발주잔량", "기준월평균", "시뮬월평균",
        "기준LT", "시뮬LT", "안전재고", "발주점", "소진일수", "판정", "기준발주량", "시뮬발주량", "증감"];
      const data = sim.map((r) => ({
        상품코드: r.code, 상품명: r.name, A코드: r.acode || "",
        현재고: r.stock || 0, 발주잔량: r.order || 0,
        기준월평균: r.baseMonthly, 시뮬월평균: r.monthly,
        기준LT: r.baseLT, 시뮬LT: r.lt,
        안전재고: Math.round(r.ss), 발주점: Math.round(r.rop),
        소진일수: r.runout == null ? "" : Math.round(r.runout),
        판정: r.signal, 기준발주량: r.baseQty, 시뮬발주량: r.qty, 증감: r.diff,
      }));
      const ws = XLSXStyle.utils.json_to_sheet(data, { header: hdrs });
      ws["!cols"] = [10, 34, 10, 9, 9, 11, 11, 8, 8, 10, 10, 10, 10, 12, 12, 9].map((w) => ({ wch: w }));
      ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };
      const wb = XLSXStyle.utils.book_new();
      XLSXStyle.utils.book_append_sheet(wb, ws, "구매시뮬레이션");
      XLSXStyle.writeFile(wb, `구매시뮬레이션_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) { /* 내보내기 실패는 화면 유지 */ }
  };

  // Tailwind는 클래스명을 정적으로 스캔하므로 `text-${tone}-700` 같은 동적 조합은 쓰지 않는다.
  const TONE = {
    slate: "border-slate-200 text-slate-700",
    orange: "border-orange-200 text-orange-700",
    rose: "border-rose-200 text-rose-600",
    emerald: "border-emerald-200 text-emerald-700",
    teal: "border-teal-200 text-teal-700",
  };
  const Card = ({ label, value, sub, tone = "slate" }) => (
    <div className={"rounded-xl border bg-white px-4 py-3 " + (TONE[tone] || TONE.slate)}>
      <div className="text-[11px] font-medium text-slate-400">{label}</div>
      <div className="mt-0.5 text-lg font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[11px] font-normal text-slate-400">{sub}</div>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-slate-50">
      <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-3">
        <SlidersHorizontal className="h-5 w-5 text-teal-600" />
        <h2 className="text-base font-bold text-slate-800">구매 시뮬레이션 ①</h2>
        <span className="text-xs text-slate-400">선택한 {nf(rows.length)}개 품목의 월평균 판매량·리드타임을 바꿔 발주 수량을 추정합니다 (저장되지 않음)</span>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={resetAll} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
            <RotateCcw className="h-3.5 w-3.5" />초기화
          </button>
          <button onClick={exportXlsx} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-800">
            <FileSpreadsheet className="h-3.5 w-3.5" />엑셀 내려받기
          </button>
          <button onClick={onClose} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
            <X className="h-3.5 w-3.5" />닫기
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="py-20 text-center text-sm text-slate-400">
          선택된 품목이 없습니다. 발주 산출 표 왼쪽 체크박스로 품목을 먼저 골라 주세요.
        </div>
      ) : (
        <div className="p-5">
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <Card label="선택 품목" value={nf(rows.length)} sub="발주 산출에서 선택" />
            <Card label="기준 발주량 합계" value={nf(sum.baseQty)} sub="현재 설정 기준" />
            <Card label="시뮬 발주량 합계" value={nf(sum.simQty)} tone="orange" sub="조정 후 추정" />
            <Card label="증감" tone={sum.diff >= 0 ? "rose" : "emerald"}
              value={(sum.diff >= 0 ? "+" : "") + nf(sum.diff)}
              sub={sum.pct == null ? "—" : `${sum.diff >= 0 ? "+" : ""}${dc(sum.pct, 1)}%`} />
            <Card label="지금발주 / 평균 소진일" tone="teal"
              value={`${nf(sum.now)}건`} sub={sum.avgCover == null ? "—" : `평균 ${dc(sum.avgCover, 0)}일분 보유`} />
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-600">
            <label className="flex items-center gap-1.5">서비스레벨
              <select value={svc} onChange={(e) => setSvc(Number(e.target.value))} className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-teal-300">
                <option value={90}>90% (z=1.28)</option><option value={95}>95% (z=1.65)</option><option value={99}>99% (z=2.33)</option>
              </select>
            </label>
            <label className="flex items-center gap-1.5">목표 재고일수
              <input type="number" min={1} value={targetDays} onChange={(e) => setTargetDays(Math.max(1, toNum(e.target.value)))} className="w-16 rounded-lg border border-slate-200 px-2 py-1 text-right text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-teal-300" />일
            </label>
            <label className="flex items-center gap-1.5 text-sky-700">월평균 일괄 조정
              <input type="number" min={0} step={5} value={bulkPct} onChange={(e) => setBulkPct(e.target.value)} className="w-20 rounded-lg border border-sky-200 bg-sky-50 px-2 py-1 text-right text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-sky-300" />%
            </label>
            <div className="flex items-center gap-1">
              {[80, 100, 120, 150].map((p) => (
                <button key={p} onClick={() => setBulkPct(p)} className={"rounded-md px-2 py-1 text-[11px] font-medium " + (Number(bulkPct) === p ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200")}>{p}%</button>
              ))}
            </div>
            <label className="flex items-center gap-1.5 text-violet-700">리드타임 일괄 가감
              <input type="number" step={1} value={bulkLtAdd} onChange={(e) => setBulkLtAdd(e.target.value)} className="w-20 rounded-lg border border-violet-200 bg-violet-50 px-2 py-1 text-right text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-violet-300" />일
            </label>
            <span className="text-slate-400">· 일괄 조정은 아래 표에서 값을 직접 입력하지 않은 품목에만 적용됩니다 · σ는 변동계수(CV)를 유지한 채 월평균에 비례해 함께 조정됩니다</span>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="w-full border-collapse text-xs table-fixed">
              <colgroup>
                <col className="w-[58px]" /><col /><col className="w-[64px]" /><col className="w-[76px]" />
                <col className="w-[52px]" /><col className="w-[64px]" /><col className="w-[58px]" />
                <col className="w-[52px]" /><col className="w-[52px]" /><col className="w-[54px]" />
                <col className="w-[48px]" /><col className="w-[74px]" /><col className="w-[62px]" />
                <col className="w-[66px]" /><col className="w-[62px]" />
              </colgroup>
              <thead><tr>
                <th className="px-1.5 py-2 text-left text-[11px] font-bold text-white bg-slate-800">코드</th>
                <th className="px-1.5 py-2 text-center text-[11px] font-bold text-white bg-slate-800">상품명</th>
                <th className="px-1.5 py-2 text-right text-[11px] font-bold text-white bg-slate-600">기준월평균</th>
                <th className="px-1.5 py-2 text-right text-[11px] font-bold text-white bg-sky-800">시뮬월평균</th>
                <th className="px-1.5 py-2 text-right text-[11px] font-bold text-white bg-slate-600">기준LT</th>
                <th className="px-1.5 py-2 text-right text-[11px] font-bold text-white bg-violet-800">시뮬LT</th>
                <th className="px-1.5 py-2 text-center text-[11px] font-bold text-white bg-sky-800">등급</th>
                <th className="px-1.5 py-2 text-right text-[11px] font-bold text-white bg-amber-700">현재고</th>
                <th className="px-1.5 py-2 text-right text-[11px] font-bold text-white bg-amber-700">발주</th>
                <th className="px-1.5 py-2 text-right text-[11px] font-bold text-white bg-teal-800">안전</th>
                <th className="px-1.5 py-2 text-right text-[11px] font-bold text-white bg-teal-800">발주점</th>
                <th className="px-1.5 py-2 text-center text-[11px] font-bold text-white bg-rose-800">판정</th>
                <th className="px-1.5 py-2 text-right text-[11px] font-bold text-white bg-slate-600">기준발주</th>
                <th className="px-1.5 py-2 text-right text-[11px] font-bold text-white bg-orange-600">시뮬발주</th>
                <th className="px-1.5 py-2 text-right text-[11px] font-bold text-white bg-orange-600">증감</th>
              </tr></thead>
              <tbody>
                {sim.map((r) => {
                  const sigCls = r.signal === "지금발주" ? "bg-red-50 text-red-600 ring-1 ring-red-200"
                    : r.signal === "곧발주" ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                      : "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200";
                  return (
                    <tr key={r.code} className={"border-b border-slate-100 " + (r.touched ? "bg-sky-50/60" : "bg-white hover:bg-slate-50")}>
                      <td className="px-1.5 py-1.5 font-mono text-[11px] text-slate-500">{r.code}</td>
                      <td className="px-1.5 py-1.5 font-medium text-slate-800"><div className="whitespace-normal break-words leading-snug line-clamp-2">{r.name}</div></td>
                      <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-400">{nf(r.baseMonthly)}</td>
                      <td className="px-1.5 py-1.5 text-right">
                        <input type="number" min={0} value={mOv[r.code] != null ? mOv[r.code] : ""} placeholder={nf(r.monthly)}
                          onChange={(e) => setMOv({ ...mOv, [r.code]: e.target.value })}
                          title="월평균 판매량을 직접 입력하면 일괄 조정 대신 이 값이 쓰입니다"
                          className="w-full rounded-md border border-sky-200 bg-white px-1 py-0.5 text-right text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-sky-300" />
                      </td>
                      <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-400">{nf(r.baseLT)}</td>
                      <td className="px-1.5 py-1.5 text-right">
                        <input type="number" min={1} value={ltOv[r.code] != null ? ltOv[r.code] : ""} placeholder={nf(r.lt)}
                          onChange={(e) => setLtOv({ ...ltOv, [r.code]: e.target.value })}
                          className="w-full rounded-md border border-violet-200 bg-white px-1 py-0.5 text-right text-xs tabular-nums focus:outline-none focus:ring-2 focus:ring-violet-300" />
                      </td>
                      <td className="px-1.5 py-1.5 text-center">
                        <span className={"inline-flex rounded-full px-1.5 py-0.5 text-[11px] font-bold " + (r.manual ? "bg-cyan-100 text-cyan-700" : r.grade === "X" ? "bg-emerald-100 text-emerald-700" : r.grade === "Y" ? "bg-amber-100 text-amber-700" : r.grade === "Z" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-400")}>{r.manual ? "수동" : r.grade || "—"}</span>
                      </td>
                      <td className="px-1.5 py-1.5 text-right tabular-nums font-semibold text-slate-800">{nf(r.stock)}</td>
                      <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-500">{r.order ? nf(r.order) : <span className="text-slate-300">—</span>}</td>
                      <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-500">{nf(Math.round(r.ss))}</td>
                      <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-600">{nf(Math.round(r.rop))}</td>
                      <td className="px-1 py-1.5 text-center"><span className={"inline-flex items-center whitespace-nowrap gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-semibold " + sigCls}>{r.signal}</span></td>
                      <td className="px-1.5 py-1.5 text-right tabular-nums text-slate-400">{r.baseQty > 0 ? nf(r.baseQty) : <span className="text-slate-300">—</span>}</td>
                      <td className="px-1.5 py-1.5 text-right tabular-nums font-bold text-orange-600">{r.qty > 0 ? nf(r.qty) : <span className="text-slate-300">—</span>}</td>
                      <td className={"px-1.5 py-1.5 text-right tabular-nums font-semibold " + (r.diff > 0 ? "text-rose-600" : r.diff < 0 ? "text-emerald-600" : "text-slate-300")}>
                        {r.diff === 0 ? "—" : (r.diff > 0 ? "+" : "") + nf(r.diff)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot><tr className="bg-slate-100 font-bold">
                <td className="px-1.5 py-2 text-slate-600" colSpan={12}>합계 ({nf(sim.length)}품목)</td>
                <td className="px-1.5 py-2 text-right tabular-nums text-slate-500">{nf(sum.baseQty)}</td>
                <td className="px-1.5 py-2 text-right tabular-nums text-orange-700">{nf(sum.simQty)}</td>
                <td className={"px-1.5 py-2 text-right tabular-nums " + (sum.diff > 0 ? "text-rose-600" : sum.diff < 0 ? "text-emerald-600" : "text-slate-400")}>
                  {sum.diff === 0 ? "—" : (sum.diff > 0 ? "+" : "") + nf(sum.diff)}
                </td>
              </tr></tfoot>
            </table>
          </div>

          <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
            수식: 시뮬발주량 = (일평균×목표일수 + 안전재고) + 일평균×리드타임 − (현재고 + 발주잔량) ·
            일평균 = 시뮬월평균 ÷ 30 · 안전재고 = z×σ×√리드타임 (Y등급 z+0.5) ·
            σ = 변동계수(CV) × 일평균 — 판매량을 늘리면 변동폭도 같은 비율로 늘어난다고 봅니다 ·
            수동 입력 품목(판매 기록 없음)은 σ=0이라 안전재고가 잡히지 않습니다.
          </p>
        </div>
      )}
    </div>
  );
}
